import { useState } from "react";
import { formatMoney } from "@shared/roi/format";
import {
  ChartEmptyState,
  ChartFrame,
  ChartLegend,
  ChartTooltip,
  HoverSlot,
  YearTick,
} from "./ChartChrome";
import {
  CHART_W,
  PAD_L,
  PAD_R,
  PAD_T,
  PLOT_H,
  PLOT_W,
  signedAxisTicks,
  tooltipLeftPct,
} from "./geometry";

/**
 * Cumulative net position, one line per scenario — the chart the comparison
 * screen is built around (docs/08-frontend.md#comparison-compare).
 *
 * *Net*, not revenue against cost: with four scenarios, eight lines would be an
 * unreadable thicket, and the question being asked here is "which of these ends
 * up ahead, and when" — which is exactly the net series. Each line is
 * direct-labelled at its end, so the reader never has to match a colour to a
 * legend entry to name a line.
 *
 * The axis is signed, because every one of these series starts below zero. Zero
 * is drawn heavier than the other gridlines: crossing it is payback, and it is
 * the only value on the ladder that means something on its own.
 */

export interface CompareSeries {
  id: string;
  name: string;
  color: string;
  /** Cumulative net by year, index 0 being the end of year 1. */
  points: number[];
}

interface Props {
  series: readonly CompareSeries[];
  /** The longest horizon in the comparison — the x axis runs to this. */
  horizonYears: number;
  currencyCode: string;
}

export function CompareNetChart({ series, horizonYears, currencyCode }: Props) {
  const [hover, setHover] = useState<number | null>(null);

  const values = series.flatMap((entry) => entry.points);
  const axis = signedAxisTicks(Math.min(0, ...values), Math.max(0, ...values));
  const isEmpty = values.length === 0 || values.every((value) => value === 0);

  const span = axis.top - axis.bottom || 1;
  const yFor = (value: number) => PAD_T + PLOT_H * (1 - (value - axis.bottom) / span);
  const xFor = (yearPosition: number) =>
    PAD_L + (yearPosition / Math.max(1, horizonYears)) * PLOT_W;
  const zeroY = yFor(0);

  const money = (value: number, compact = false) =>
    formatMoney(currencyCode, value, { compact, signed: true });

  // Labels are placed after every line is drawn, so two scenarios that end at
  // nearly the same figure can be nudged apart instead of printing on top of
  // each other. Sorted by y, then pushed down until each clears the last.
  const ends = series
    .map((entry) => {
      const last = entry.points[entry.points.length - 1];
      return last === undefined
        ? null
        : {
            id: entry.id,
            color: entry.color,
            value: last,
            x: xFor(entry.points.length),
            y: yFor(last),
          };
    })
    .filter((end): end is NonNullable<typeof end> => end !== null)
    .sort((a, b) => a.y - b.y);

  let previousLabelY = -Infinity;
  const labels = ends.map((end) => {
    const y = Math.max(end.y, previousLabelY + 11);
    previousLabelY = y;
    return { ...end, labelY: Math.min(PAD_T + PLOT_H - 2, y) };
  });

  return (
    <ChartFrame
      label="Cumulative net position of each scenario, by year"
      overlay={
        <>
          {isEmpty && (
            <ChartEmptyState message="None of these scenarios has any figures yet — save one and its line appears here." />
          )}
          {hover !== null && !isEmpty && (
            <ChartTooltip
              leftPct={tooltipLeftPct(xFor(hover + 1))}
              title={`Through year ${hover + 1}`}
              rows={series.map((entry) => ({
                label: entry.name,
                value:
                  entry.points[hover] === undefined
                    ? "beyond its horizon"
                    : money(entry.points[hover]),
                color: entry.color,
              }))}
            />
          )}
        </>
      }
      footer={
        <ChartLegend
          items={series.map((entry) => ({
            label: entry.name,
            color: entry.color,
            shape: "line" as const,
          }))}
        />
      }
    >
      <g aria-hidden="true">
        {axis.ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD_L}
              x2={CHART_W - PAD_R}
              y1={yFor(tick)}
              y2={yFor(tick)}
              stroke="var(--line)"
              strokeWidth={tick === 0 ? 1.5 : 1}
              opacity={tick === 0 ? 1 : 0.7}
            />
            <text
              x={PAD_L - 8}
              y={yFor(tick) + 3}
              textAnchor="end"
              className="tabular fill-[var(--ink-soft)] text-[11px]"
            >
              {formatMoney(currencyCode, tick, { compact: true, signed: true })}
            </text>
          </g>
        ))}
        <text
          x={CHART_W - PAD_R + 4}
          y={zeroY + 3}
          className="fill-[var(--ink-soft)] text-[9px] font-semibold"
        >
          break even
        </text>
      </g>

      {!isEmpty &&
        series.map((entry) => {
          // Every line starts at zero at the start of year 1, which is what
          // makes two scenarios with different horizons comparable at all.
          const path = [
            { x: xFor(0), y: zeroY },
            ...entry.points.map((value, index) => ({
              x: xFor(index + 1),
              y: yFor(value),
            })),
          ]
            .map(
              (point, index) =>
                `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`,
            )
            .join(" ");

          return (
            <path
              key={entry.id}
              d={path}
              fill="none"
              stroke={entry.color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}

      {!isEmpty &&
        labels.map((end) => (
          <g key={end.id}>
            <circle
              cx={end.x}
              cy={end.y}
              r={3.5}
              fill={end.color}
              stroke="var(--surface-strong)"
              strokeWidth={2}
            />
            <text
              x={end.x - 6}
              y={end.labelY - 5}
              textAnchor="end"
              className="tabular text-[11px] font-semibold"
              fill={end.color}
            >
              {money(end.value, true)}
            </text>
          </g>
        ))}

      {hover !== null && !isEmpty && (
        <line
          x1={xFor(hover + 1)}
          x2={xFor(hover + 1)}
          y1={PAD_T}
          y2={PAD_T + PLOT_H}
          stroke="var(--ink-soft)"
          strokeWidth={1}
          opacity={0.3}
        />
      )}

      {Array.from({ length: horizonYears }, (_, index) => (
        <g key={index}>
          <YearTick x={xFor(index + 1)} year={index + 1} />
          <HoverSlot
            x={PAD_L + (index / Math.max(1, horizonYears)) * PLOT_W}
            width={PLOT_W / Math.max(1, horizonYears)}
            onEnter={() => setHover(index)}
            onLeave={() => setHover(null)}
          />
        </g>
      ))}
    </ChartFrame>
  );
}
